import { Injectable } from '@angular/core';

import { environment } from 'src/app/environments/environment';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import {
  CreateIMSproductRequest,
  IMSproductResponse,
  PaginatedIMSproductsResponse,
  ProductAttribute,
  ProductCategoryType,
  ProductImageModel,
  ProductInternalStatus,
  ProductListingStatus,
  ProductPostingProfileRuleMappingModel,
} from '../models/product.model';
import { HttpParamsService } from 'src/app/shared/services/http-params.service';

@Injectable({
  providedIn: 'root',
})
export class ProductService {
  imsBaseUrl: string = environment.imsApiBaseUrl;
  listerBaseUrl: string = environment.listerApiBaseUrl;
  companyBaseUrl: string = environment.companyApiBaseUrl;

  constructor(
    private http: HttpClient,
    private httpParamsService: HttpParamsService,
  ) {}

  // product crud endpoints
  getProducts(
    companyId: number,
    warehouseId: number,
    pageNumber?: number,
    pageSize?: number,
    searchTerm?: string,
    sortCriteria?: string,
  ): Observable<PaginatedIMSproductsResponse> {
    let params = new HttpParams().set('companyId', companyId.toString());

    if (pageNumber != null) {
      params = params.set('pageNumber', pageNumber.toString());
    }

    if (pageSize != null) {
      params = params.set('pageSize', pageSize.toString());
    }

    if (searchTerm?.trim()) {
      params = params.set('searchTerm', searchTerm.trim());
    }

    if (sortCriteria != null) {
      params = params.set('sortCriteria', sortCriteria.trim());
    }

    params = params.set('warehouseId', warehouseId.toString());
    params = params.set('source', 3);
    return this.http.get<PaginatedIMSproductsResponse>(
      `${this.imsBaseUrl}/Products`,
      {
        params,
      },
    );
  }

  getProductById(
    productId: number,
    WarehouseId: number,
  ): Observable<IMSproductResponse> {
    const url = `${this.imsBaseUrl}/Products/${productId}?warehouseId=${WarehouseId}`;
    return this.http.get<IMSproductResponse>(url);
  }

  createProduct(product: CreateIMSproductRequest) {
    const url = `${this.imsBaseUrl}/Products`;
    return this.http.post<IMSproductResponse>(url, product);
  }

  editProduct(data: CreateIMSproductRequest) {
    let productId = data.id;
    const url = `${this.imsBaseUrl}/Products/${productId}`;
    return this.http.put<IMSproductResponse>(url, data);
  }

  retryAiProcessing(productId: number) {
    const url = `${this.imsBaseUrl}/Products/${productId}/Status?InternalStatus=${ProductInternalStatus.AIProcessing}`;
    return this.http.put<IMSproductResponse>(url, null);
  }

  retryListing(productId: number) {
    const url = `${this.imsBaseUrl}/Products/${productId}/Status?ListingStatus=${ProductListingStatus.ReadyForListing}`;
    return this.http.put<IMSproductResponse>(url, null);
  }

  //images endpoints
  uploadImage(companyId: number, productId: number, file: File | undefined) {
    const formData = new FormData();
    formData.append('File', file || '');
    const url = `${this.imsBaseUrl}/BlobFiles/Upload?path=products&companyId=${companyId}`;
    return this.http.post<ProductImageModel>(url, formData);
  }

  mapProductImages(productId: number, data: ProductImageModel) {
    const url = `${this.imsBaseUrl}/Products/${productId}/Images`;
    return this.http.post<ProductImageModel>(url, data);
  }
  deleteProductImage(productId: number, imageId: number) {
    const url = `${this.imsBaseUrl}/Products/${productId}/Images/${imageId}`;
    return this.http.delete(url);
  }

  getProductImages(productId: number): Observable<ProductImageModel[]> {
    const url = `${this.imsBaseUrl}/Products/${productId}/Images`;
    return this.http.get<ProductImageModel[]>(url);
  }
  getProductImageById(
    productId: number,
    imageId: number,
  ): Observable<ProductImageModel> {
    const url = `${this.imsBaseUrl}/Products/${productId}/Images/${imageId}`;
    return this.http.get<ProductImageModel>(url);
  }
  editProductImage(
    productId: number,
    data: ProductImageModel,
  ): Observable<ProductImageModel> {
    const url = `${this.imsBaseUrl}/Products/${productId}/Images/${data.id}`;
    return this.http.put<ProductImageModel>(url, data);
  }

  //posting profile mappings
  getPostingProfileMappings(
    productId: number,
  ): Observable<ProductPostingProfileRuleMappingModel[]> {
    const url = `${this.listerBaseUrl}/ProductPostingProfileRuleMappings?ProductId=${productId}`;
    return this.http.get<ProductPostingProfileRuleMappingModel[]>(url);
  }
  getPostingProfileMappingById(
    id: number,
  ): Observable<ProductPostingProfileRuleMappingModel> {
    const url = `${this.listerBaseUrl}/ProductPostingProfileRuleMappings/${id}`;
    return this.http.get<ProductPostingProfileRuleMappingModel>(url);
  }
  createPostingProfileMapping(
    data: ProductPostingProfileRuleMappingModel,
  ): Observable<ProductPostingProfileRuleMappingModel> {
    const url = `${this.listerBaseUrl}/ProductPostingProfileRuleMappings`;
    return this.http.post<ProductPostingProfileRuleMappingModel>(url, data);
  }
  editPostingProfileMapping(
    data: ProductPostingProfileRuleMappingModel,
  ): Observable<ProductPostingProfileRuleMappingModel> {
    const url = `${this.listerBaseUrl}/ProductPostingProfileRuleMappings/${data.id}`;
    return this.http.put<ProductPostingProfileRuleMappingModel>(url, data);
  }
  deletePostingProfileMapping(id: number): Observable<void> {
    const url = `${this.listerBaseUrl}/ProductPostingProfileRuleMappings/${id}`;
    return this.http.delete<void>(url);
  }
  
  getProductAttributesByCategoryTypeId(
    queryParams: Record<string, any>,
  ): Observable<ProductAttribute[]> {
    const params: HttpParams =
      this.httpParamsService.buildCleanHttpParams(queryParams);

    return this.http.get<ProductAttribute[]>(
      `${this.companyBaseUrl}/ProductAttributes`,
      { params },
    );
  }

  getProductCategoryTypeById(
    categoryTypeId: number,
  ): Observable<ProductCategoryType> {
    const url = `${this.companyBaseUrl}/ProductCategoryTypes/${categoryTypeId}`;
    return this.http.get<ProductCategoryType>(url);
  }
}
